import { useToast } from '@cian/ui-kit/toast';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { requestAuthentication } from '../../actions/authentication/requestAuthentication';
import {
  ADD_TO_COMPARISON,
  DELETE_FROM_COMPARISON,
  IAddToComparison,
  IDeleteFromComparison,
} from '../../constants/actionTypes/offersComparison';
import {
  NEWBUILDING_COMPARE_ENABLED_CONFIG,
  NEWBUILDING_COMPARE_MOBILE_ENABLED_CONFIG,
} from '../../constants/newbuildingCompare';
import { NewbuildingComparisonButton } from '../../components/NewbuildingComparisonButton';
import { NewbuildingComparisonIconButton } from '../../components/NewbuildingComparisonIconButton';
import {
  EStatus,
  IComparisonStatusInfoSchema,
} from '../../repositories/favorites/entities/comparison_status_info/ComparisonStatusInfoSchema';
import { selectUser } from '../../selectors/user';
import {
  addNewbuildingToComparison,
  type IComparisonActionResult,
  removeNewbuildingFromComparison,
} from '../../services/propertyComparison';
import { useApplicationContext } from '../../utils/applicationContext';
import { CompareToastContent } from './CompareToastContent';
import {
  trackAddNewbuildingToComparison,
  trackClickAddComparisonPopup,
  trackClickRemoveComparisonPopup,
  trackDeleteNewbuildingFromComparison,
} from './tracking';

const COMPARISON_TOAST_HIDE_DELAY = 5000;
const COMPARISON_ERROR_TEXT = 'Не удалось изменить сравнение. Попробуйте еще раз';

interface INewbuildingComparisonButtonContainerProps {
  entityId: number;
  label: string;
  comparisonStatus: IComparisonStatusInfoSchema;
  appearance?: 'button' | 'icon';
}

export const NewbuildingComparisonButtonContainer: React.FC<INewbuildingComparisonButtonContainerProps> = ({
  entityId,
  label,
  comparisonStatus,
  appearance = 'button',
}) => {
  const { config, httpApi, logger } = useApplicationContext();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const toast = useToast({ placement: 'bottom-right', hideDelay: COMPARISON_TOAST_HIDE_DELAY, size: 'L' });
  const isIconAppearance = appearance === 'icon';
  const isNewbuildingCompareEnabled =
    config.get<boolean>(
      isIconAppearance ? NEWBUILDING_COMPARE_MOBILE_ENABLED_CONFIG : NEWBUILDING_COMPARE_ENABLED_CONFIG,
    ) || false;

  const isAdded = comparisonStatus.status === EStatus.Added;
  const isUnavailable = comparisonStatus.status === EStatus.Unavailable;

  const showComparisonToast = React.useCallback(
    (result: IComparisonActionResult, isRemoved: boolean) => {
      // В мобильной карточке ЖК снэкбар не поднимаем
      if (isIconAppearance) {
        return;
      }

      toast.open({
        type: result.success === false ? 'error' : 'success',
        hasCloseIcon: false,
        content: (
          <CompareToastContent
            text={result.text}
            comparisonUrl={result.comparisonUrl}
            onClick={() =>
              isRemoved ? trackClickRemoveComparisonPopup(entityId) : trackClickAddComparisonPopup(entityId)
            }
          />
        ),
      });
    },
    [toast, isIconAppearance, entityId],
  );

  const handleClick = React.useCallback(
    async (event?: React.MouseEvent<HTMLButtonElement>) => {
      // В мобильной карточке группа кнопок лежит поверх кликабельной области карточки
      if (isIconAppearance) {
        event?.stopPropagation();
        event?.preventDefault();
      }

      if (!user.isAuthenticated) {
        dispatch(requestAuthentication());

        return;
      }

      dispatch<IAddToComparison | IDeleteFromComparison>(
        isAdded ? { type: DELETE_FROM_COMPARISON, offerId: entityId } : { type: ADD_TO_COMPARISON, offerId: entityId },
      );

      try {
        const result = isAdded
          ? await removeNewbuildingFromComparison({ httpApi, logger, newbuildingId: entityId })
          : await addNewbuildingToComparison({ httpApi, logger, newbuildingId: entityId });

        showComparisonToast(result, isAdded);

        if (result.success === false) {
          dispatch<IAddToComparison | IDeleteFromComparison>(
            isAdded
              ? { type: ADD_TO_COMPARISON, offerId: entityId }
              : { type: DELETE_FROM_COMPARISON, offerId: entityId },
          );

          return;
        }

        if (isAdded) {
          trackDeleteNewbuildingFromComparison({ entityId, label });
        } else {
          trackAddNewbuildingToComparison({ entityId, label });
        }
      } catch {
        dispatch<IAddToComparison | IDeleteFromComparison>(
          isAdded
            ? { type: ADD_TO_COMPARISON, offerId: entityId }
            : { type: DELETE_FROM_COMPARISON, offerId: entityId },
        );
        showComparisonToast({ success: false, text: COMPARISON_ERROR_TEXT }, isAdded);
      }
    },
    [dispatch, entityId, httpApi, isAdded, isIconAppearance, label, logger, showComparisonToast, user.isAuthenticated],
  );

  if (!isNewbuildingCompareEnabled || isUnavailable) {
    return null;
  }

  if (isIconAppearance) {
    return <NewbuildingComparisonIconButton isAdded={isAdded} onClick={handleClick} />;
  }

  return <NewbuildingComparisonButton isAdded={isAdded} onClick={handleClick} />;
};
